import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Users, DoorOpen, FileBarChart } from "lucide-react";
import {
  createChildOrg,
  deactivateChildOrg,
  fetchTraegerOrgs,
  type CreateChildOrgInput,
  type TraegerChildListItem,
} from "./traeger-api";
import { APIError } from "@/lib/api-client";

/**
 * Träger-Orgs-Route — list aller child-orgs + create-modal.
 *
 * Server validiert min/max/tier-rules. UI zeigt klare fehlermeldungen
 * + disabled "Anlegen"-button wenn max erreicht.
 */
export function TraegerOrgsRoute(): React.ReactElement {
  const qc = useQueryClient();
  const orgs = useQuery({
    queryKey: ["traeger", "orgs"],
    queryFn: fetchTraegerOrgs,
    staleTime: 60 * 1000,
  });
  const [createOpen, setCreateOpen] = React.useState(false);

  const deleteMutation = useMutation({
    mutationFn: deactivateChildOrg,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["traeger"] }),
  });

  if (orgs.isPending) {
    return <div className="h-32 animate-pulse rounded bg-neutral-100" />;
  }
  if (orgs.isError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900">
        Fehler beim Laden der Einrichtungs-Liste.
      </div>
    );
  }

  const data = orgs.data;
  const maxReached = data.current_count >= data.max_orgs;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Einrichtungen</h1>
          <p className="text-sm text-neutral-600">
            {data.current_count} von {data.max_orgs} (Mindest: {data.min_orgs})
          </p>
        </div>
        <button
          type="button"
          disabled={maxReached}
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1 rounded-md bg-[color:var(--color-warm-orange-700)] px-3 py-2 text-sm font-medium text-white hover:bg-[color:var(--color-warm-orange-800)] disabled:cursor-not-allowed disabled:bg-neutral-300"
        >
          <Plus className="size-4" />
          Neue Einrichtung
        </button>
      </header>

      {maxReached && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
          Maximum {data.max_orgs} Einrichtungen erreicht. Für mehr bitte
          Enterprise-Tarif anfragen.
        </div>
      )}

      {data.items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-8 text-center text-sm text-neutral-600">
          Noch keine Einrichtungen angelegt.
        </div>
      ) : (
        <ul className="divide-y divide-neutral-200 rounded-lg border border-neutral-200 bg-white">
          {data.items.map((item) => (
            <ChildOrgRow
              key={item.id}
              item={item}
              onDelete={() => {
                if (confirm(`Einrichtung "${item.name}" deaktivieren?`)) {
                  deleteMutation.mutate(item.id);
                }
              }}
              deleting={deleteMutation.isPending && deleteMutation.variables === item.id}
            />
          ))}
        </ul>
      )}

      {createOpen && (
        <CreateChildOrgModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ["traeger"] });
            setCreateOpen(false);
          }}
        />
      )}
    </div>
  );
}

function ChildOrgRow({
  item,
  onDelete,
  deleting,
}: {
  item: TraegerChildListItem;
  onDelete: () => void;
  deleting: boolean;
}): React.ReactElement {
  return (
    <li className="flex flex-wrap items-center gap-4 p-4">
      <div className="flex-1">
        <div className="flex items-baseline gap-2">
          <h3 className="text-base font-semibold text-neutral-900">{item.name}</h3>
          <span className="rounded-full bg-[color:var(--color-warm-orange-50)] px-2 py-0.5 text-xs font-medium text-[color:var(--color-warm-orange-900)]">
            {item.tier}
          </span>
          {!item.is_active && (
            <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-xs font-medium text-neutral-700">
              Deaktiviert
            </span>
          )}
        </div>
        {(item.plz || item.city) && (
          <p className="text-sm text-neutral-600">
            {item.plz} {item.city}
          </p>
        )}
      </div>
      <dl className="flex gap-4 text-sm">
        <div title="Aktive Nutzer">
          <Users className="inline size-3.5 text-neutral-500" /> {item.user_count}
        </div>
        <div title="Räume">
          <DoorOpen className="inline size-3.5 text-neutral-500" /> {item.room_count}
        </div>
        <div title="Einträge letzte 30 Tage">
          <FileBarChart className="inline size-3.5 text-neutral-500" /> {item.entries_30d}
        </div>
      </dl>
      <button
        type="button"
        onClick={onDelete}
        disabled={deleting}
        className="rounded-md border border-neutral-300 p-2 text-neutral-600 hover:border-red-300 hover:text-red-700 disabled:opacity-50"
        aria-label={`${item.name} deaktivieren`}
      >
        <Trash2 className="size-4" />
      </button>
    </li>
  );
}

function CreateChildOrgModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}): React.ReactElement {
  const dialogRef = React.useRef<HTMLDialogElement>(null);
  const [form, setForm] = React.useState<CreateChildOrgInput>({
    name: "",
    tier: "M",
    plz: "",
    city: "",
  });
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    dialogRef.current?.showModal();
    return () => dialogRef.current?.close();
  }, []);

  const createMutation = useMutation({
    mutationFn: createChildOrg,
    onSuccess: () => {
      onCreated();
    },
    onError: (err) => {
      if (err instanceof APIError) {
        if (err.code === "max_children_exceeded") {
          setError(`Maximum erreicht. Kein neues Anlegen möglich.`);
        } else if (err.code === "name_conflict") {
          setError("Eine Einrichtung mit diesem Namen existiert bereits.");
        } else {
          setError(err.message || err.code);
        }
      } else {
        setError("Netzwerk-Fehler. Bitte erneut versuchen.");
      }
    },
  });

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="rounded-lg p-0 backdrop:bg-black/50 max-sm:rounded-b-none"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          createMutation.mutate(form);
        }}
        className="w-[min(28rem,95vw)] p-6 space-y-4"
      >
        <h2 className="text-lg font-semibold">Neue Einrichtung</h2>

        <label className="block">
          <span className="text-sm font-medium text-neutral-700">Name</span>
          <input
            type="text"
            required
            maxLength={200}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-1 block w-full rounded-md border-neutral-300 text-sm"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-neutral-700">Tarif</span>
          <select
            value={form.tier}
            onChange={(e) => setForm({ ...form, tier: e.target.value as "S" | "M" | "L" })}
            className="mt-1 block w-full rounded-md border-neutral-300 text-sm"
          >
            <option value="S">S — Kita / kleine Grundschule (≤100 Kinder)</option>
            <option value="M">M — Grundschule (≤300 Kinder)</option>
            <option value="L">L — Großschule (≤800 Kinder)</option>
          </select>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-sm font-medium text-neutral-700">PLZ</span>
            <input
              type="text"
              required
              maxLength={5}
              pattern="\d{5}"
              value={form.plz}
              onChange={(e) => setForm({ ...form, plz: e.target.value })}
              className="mt-1 block w-full rounded-md border-neutral-300 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-neutral-700">Ort</span>
            <input
              type="text"
              required
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="mt-1 block w-full rounded-md border-neutral-300 text-sm"
            />
          </label>
        </div>

        {error && (
          <p className="rounded-md bg-red-50 p-2 text-sm text-red-900">{error}</p>
        )}

        <footer className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-900 hover:bg-neutral-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="rounded-md bg-[color:var(--color-warm-orange-700)] px-4 py-2 text-sm font-medium text-white hover:bg-[color:var(--color-warm-orange-800)] disabled:opacity-50"
          >
            {createMutation.isPending ? "Wird angelegt …" : "Anlegen"}
          </button>
        </footer>
      </form>
    </dialog>
  );
}
